import React, { useEffect, useState } from "react";
import { Modal, Button, Typography, Result, Divider, Space, message, Alert, notification } from "antd";
import { 
  DownloadOutlined, CloudSyncOutlined, ExportOutlined, LockOutlined,
  CopyOutlined, CustomerServiceOutlined,
  ReloadOutlined, RocketFilled, FormOutlined
} from "@ant-design/icons";
import { usePWA } from "@seims/shared";
import { SUPPORT_EMAIL } from "../../config/constants";
import { getSupportId } from "../../utils/support";
import PwaInstallSupportNotice from "./PwaInstallSupportNotice";

const { Text, Title } = Typography;

const InstallPrompt = () => {
  const { isInstallable, wasInstalled, triggerInstall, updateAvailable, reloadToUpdate, isOnline } = usePWA();
  const [isStandalone, setIsStandalone] = useState(false);
  const [showManualInstructions, setShowManualInstructions] = useState(false);

  const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isSecureOrigin = window.isSecureContext || ["localhost", "127.0.0.1", "::1"].includes(window.location.hostname);
  const currentId = getSupportId();
  const shouldOfferLaunchFallback =
    !updateAvailable &&
    !isInstallable &&
    !isIOS &&
    !isStandalone &&
    !isFirefox &&
    isSecureOrigin &&
    showManualInstructions;

  useEffect(() => {
    const checkStandalone = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
      setIsStandalone(standalone);
    };

    checkStandalone();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    mediaQuery.addEventListener('change', checkStandalone);
    
    // Firefox shows the "Launch" option immediately because it won't ever trigger 'isInstallable'
    const timer = setTimeout(() => setShowManualInstructions(true), isFirefox ? 500 : 3000);

    return () => {
      mediaQuery.removeEventListener('change', checkStandalone);
      clearTimeout(timer);
    };
  }, [isFirefox]);

  if (isStandalone && !updateAvailable) return null;

  const handleLaunchApp = () => {
    const hide = message.loading("Attempting to launch EventsApp...", 0);
    const start = Date.now();
    
    // Attempt to trigger the OS-level protocol
    window.location.href = "web+eventsapp://launch";

    // Heartbeat check: If the user stays in the browser without a popup, 
    // the duration between now and the timeout will be very short.
    setTimeout(() => {
      hide();
      const duration = Date.now() - start;

      if (duration < 600) {
        notification.warning({
          message: 'App Not Detected',
          description: "We couldn't find the app on this device. Please install it using Chrome or Edge first.",
          placement: 'topRight',
          duration: 8
        });
      } else {
        notification.info({
          message: 'Check for Popup ↗️',
          description: "Click 'Allow' or 'Open' in the browser prompt to switch to the app window.",
          placement: 'topRight',
          style: { border: '2px solid #1890ff', marginTop: '20px' }
        });
      }
    }, 1000);
  };

  return (
    <Modal
      open={true}
      centered
      closable={false}
      footer={null}
      zIndex={2000}
      width={420}
      modalRender={(modalNode) => <div data-testid="install-prompt-modal">{modalNode}</div>}
    >
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <img src="/icons/app-icon-64x64.webp" alt="Logo" style={{ width: 64, borderRadius: 16, marginBottom: 12 }} />
        <Title level={4}>Events App</Title>
      </div>

      {!isOnline && (
        <Alert
          style={{ marginBottom: 16 }}
          showIcon
          type="warning"
          message="You are offline"
          description={
            <Button
              type="primary"
              icon={<FormOutlined />}
              onClick={() => window.location.assign('/events/offline-capture')}
            >
              Open Data Capture Page
            </Button>
          }
        />
      )}

      <PwaInstallSupportNotice />

      {updateAvailable && (
        <Result status="warning" title="Update Required" 
          extra={<Button type="primary" icon={<CloudSyncOutlined />} onClick={reloadToUpdate} block size="large">Update & Restart</Button>} 
        />
      )}

      {/* CHROME/EDGE: browser can prompt install */}
      {!updateAvailable && isInstallable && !isIOS && !isStandalone && (
        <Result status="success" title="Ready to Install" 
          extra={<Button data-testid="install-app-button" type="primary" icon={<DownloadOutlined />} onClick={triggerInstall} block size="large">Install App</Button>} 
        />
      )}

      {/* IOS: no beforeinstallprompt, show manual steps */}
      {!updateAvailable && !isInstallable && isIOS && !isStandalone && (
        <div style={{ textAlign: 'center' }}>
          <Alert message="iOS Setup" type="info" showIcon />
          <div style={{ background: '#f5f5f5', padding: '16px', borderRadius: '12px', textAlign: 'left', marginTop: 16 }}>
            <p>1. Tap **Share** <ExportOutlined style={{ color: '#1890ff' }} /></p>
            <p style={{ marginBottom: 0 }}>2. Select **"Add to Home Screen"**</p>
          </div>
          <Button block onClick={() => window.location.reload()} style={{ marginTop: 16 }} icon={<ReloadOutlined />}>Check Status</Button>
        </div>
      )}

      {/* ALREADY INSTALLED: offer launch bridge */}
      {!updateAvailable && !isInstallable && !isIOS && !isStandalone && (wasInstalled || shouldOfferLaunchFallback) && (
        <div style={{ textAlign: 'center' }}>
          {showManualInstructions ? (
            <>
              <Result 
                icon={<RocketFilled style={{ color: '#1890ff' }} />} 
                title={wasInstalled ? "Switch to App Window" : "Open Installed App"}
                subTitle={
                  wasInstalled
                    ? "The app is installed - open it from your home screen or use the button below."
                    : "If Events App is already installed on this device, use the button below to switch to it."
                }
              />
              <div style={{ background: '#e6f7ff', border: '1px solid #91d5ff', padding: '16px', borderRadius: '8px', textAlign: 'left', marginBottom: 16 }}>
                <Button type="primary" block icon={<ExportOutlined />} onClick={handleLaunchApp} size="large">
                  {wasInstalled ? 'Launch App Window' : 'Open Installed App'}
                </Button>
                <Divider plain><Text type="secondary" style={{ fontSize: '11px' }}>GUIDE</Text></Divider>
                <ol style={{ paddingLeft: 20, marginTop: 8, fontSize: '12px', lineHeight: '1.6' }}>
                  <li>Click <strong>"Allow"</strong> if the browser asks to open the link.</li>
                  <li>Look for <strong>"Open in app"</strong> in your address bar.</li>
                </ol>
              </div>
              <Button block onClick={() => window.location.reload()} icon={<ReloadOutlined />}>Check Status</Button>
            </>
          ) : (
            <Result icon={<LockOutlined style={{ color: '#faad14' }} />} title="Verifying Connection..." />
          )}
        </div>
      )}

      {/* CANNOT INSTALL: insecure origin (HTTP LAN), no prior install recorded */}
      {!updateAvailable && !isInstallable && !isIOS && !isStandalone && !isFirefox && !wasInstalled && !isSecureOrigin && (
        <Result
          status="warning"
          title="Install Not Available"
          subTitle="Browser install requires a secure connection (HTTPS) or localhost. Open this app via its official link to install."
          extra={<Button block onClick={() => window.location.reload()} icon={<ReloadOutlined />}>Check Again</Button>}
        />
      )}

      {/* SECURE ORIGIN: install eligibility still being evaluated by browser */}
      {!updateAvailable && !isInstallable && !isIOS && !isStandalone && !isFirefox && !wasInstalled && isSecureOrigin && !showManualInstructions && (
        <Result
          icon={<LockOutlined style={{ color: '#faad14' }} />}
          title="Checking Install Availability..."
          subTitle="If this persists, refresh once and ensure you are using Chrome or Edge."
          extra={<Button block onClick={() => window.location.reload()} icon={<ReloadOutlined />}>Check Again</Button>}
        />
      )}

      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <Divider plain><Text type="secondary" style={{ fontSize: '11px' }}>Support</Text></Divider>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button type="link" size="small" icon={<CustomerServiceOutlined />} onClick={() => window.location.href = `mailto:${SUPPORT_EMAIL}`}>Technical Support</Button>
          <Text type="secondary" style={{ fontSize: '10px', cursor: 'pointer' }} onClick={() => { navigator.clipboard.writeText(currentId); message.success("ID Copied"); }}>
            ID: {currentId} <CopyOutlined />
          </Text>
        </Space>
      </div>
    </Modal>
  );
};

export default InstallPrompt;